import type { LayoutItem, LayoutPage } from "./extract";

const PAGE_WIDTH = 595;
const LINE_HEIGHT = 16;
const CHAR_WIDTH = 5.4;
const HEADING_SIZES: Record<string, number> = { h1: 20, h2: 16, h3: 14 };

const textOf = (element: Element): string =>
  (element.textContent ?? "").replace(/\s+/g, " ").trim();

/** Paragraphe entièrement (ou presque) en gras. */
const isBold = (element: Element): boolean => {
  const total = textOf(element).length;
  if (!total) return false;
  const bold = Array.from(element.querySelectorAll("strong, b")).reduce(
    (sum, node) => sum + textOf(node).length,
    0,
  );
  return bold >= total * 0.6;
};

/** Lignes d'un paragraphe : un saut de ligne manuel (<br>) ouvre une nouvelle ligne. */
const linesOf = (element: Element): string[] => {
  const clone = element.cloneNode(true) as Element;
  for (const br of Array.from(clone.querySelectorAll("br"))) br.replaceWith("\n");
  return (clone.textContent ?? "")
    .split("\n")
    .map((line) => line.replace(/\s+/g, " ").trim())
    .filter(Boolean);
};

export const docxHtmlToPages = (html: string): LayoutPage[] => {
  const document = new DOMParser().parseFromString(html, "text/html");
  const items: LayoutItem[] = [];
  let top = 0;

  const line = (text: string, options: { x?: number; height?: number; bold?: boolean } = {}) => {
    const height = options.height ?? 12;
    const x = options.x ?? 0;
    items.push({
      text,
      x,
      top,
      width: Math.min(PAGE_WIDTH - x, text.length * CHAR_WIDTH * (height / 12)),
      height,
      bold: options.bold ?? false,
      font: options.bold ? "Word-Bold" : "Word",
    });
    top += Math.max(LINE_HEIGHT, height + 4);
  };

  const list = (element: Element, depth: number) => {
    for (const item of Array.from(element.children)) {
      if (item.tagName.toLowerCase() !== "li") continue;
      const own = item.cloneNode(true) as Element;
      for (const nested of Array.from(own.querySelectorAll("ul, ol"))) nested.remove();
      const text = textOf(own);
      if (text) line(`• ${text}`, { x: 12 * depth, bold: isBold(own) });
      for (const nested of Array.from(item.children)) {
        if (/^(ul|ol)$/i.test(nested.tagName)) list(nested, depth + 1);
      }
    }
  };

  const table = (element: Element) => {
    for (const row of Array.from(element.querySelectorAll("tr"))) {
      const cells = Array.from(row.children).filter((cell) => /^(td|th)$/i.test(cell.tagName));
      if (!cells.length) continue;
      const width = PAGE_WIDTH / cells.length;
      const rowTop = top;
      let rowHeight = 1;
      cells.forEach((cell, index) => {
        // Une cellule sur plusieurs paragraphes : une ligne par paragraphe, dans sa colonne.
        const texts = Array.from(cell.querySelectorAll("p, li"))
          .map((node) => (node.tagName.toLowerCase() === "li" ? `• ${textOf(node)}` : textOf(node)))
          .filter(Boolean);
        const lines = texts.length ? texts : [textOf(cell)].filter(Boolean);
        lines.forEach((text, offset) => {
          items.push({
            text,
            x: index * width,
            top: rowTop + offset * LINE_HEIGHT,
            width: Math.min(width - 10, text.length * CHAR_WIDTH),
            height: 12,
            bold: isBold(cell),
            font: isBold(cell) ? "Word-Bold" : "Word",
          });
        });
        rowHeight = Math.max(rowHeight, lines.length);
      });
      top = rowTop + rowHeight * LINE_HEIGHT + 6;
    }
  };

  const walk = (parent: Element) => {
    for (const element of Array.from(parent.children)) {
      const tag = element.tagName.toLowerCase();
      if (tag in HEADING_SIZES || /^h[4-6]$/.test(tag)) {
        const text = textOf(element);
        if (text) line(text, { bold: true, height: HEADING_SIZES[tag] ?? 13 });
      } else if (tag === "p") {
        const bold = isBold(element);
        for (const text of linesOf(element)) line(text, { bold });
      } else if (tag === "ul" || tag === "ol") {
        list(element, 1);
      } else if (tag === "table") {
        table(element);
      } else {
        walk(element);
      }
    }
  };

  walk(document.body);
  return [{ width: PAGE_WIDTH, height: top + 40, items }];
};
